'use client';

import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

type CanvasContextType = {
  isPanMode: boolean;
  togglePanMode: () => void;
  setPanMode: (enabled: boolean) => void;
  isPreviewMode: boolean;
  togglePreviewMode: () => void;
  setPreviewMode: (enabled: boolean) => void;
};

export const CanvasContext = createContext<CanvasContextType | null>(null);

export const useCanvas = () => {
  const context = useContext(CanvasContext);

  if (!context) {
    throw new Error('useCanvas must be used within a CanvasProvider');
  }

  return context;
};

export const CanvasProvider = ({ children }: { children: ReactNode }) => {
  const [isPanMode, setIsPanMode] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  const togglePanMode = useCallback(() => {
    setIsPanMode((prev) => !prev);
  }, []);

  const setPanMode = useCallback((enabled: boolean) => {
    setIsPanMode(enabled);
  }, []);

  const togglePreviewMode = useCallback(() => {
    setIsPreviewMode((prev) => !prev);
  }, []);

  const setPreviewMode = useCallback((enabled: boolean) => {
    setIsPreviewMode(enabled);
  }, []);

  const value = useMemo(
    () => ({
      isPanMode,
      togglePanMode,
      setPanMode,
      isPreviewMode,
      togglePreviewMode,
      setPreviewMode,
    }),
    [
      isPanMode,
      togglePanMode,
      setPanMode,
      isPreviewMode,
      togglePreviewMode,
      setPreviewMode,
    ],
  );

  return (
    <CanvasContext.Provider value={value}>
      {children}
    </CanvasContext.Provider>
  );
};